import type { CommandHistory } from '@/core/CommandHistory';
import { clear, el } from './dom';
import { icon } from './Icons';
import {
  historyIcon,
  historyRowTitle,
  historyRows,
  historySummary,
  type HistoryRow,
} from './HistoryView';

/**
 * The operations history — every edit of this sitting, in order, with the current position marked.
 *
 * ## Why a list and not just two buttons
 *
 * Undo and redo answer "take back the last thing". They do not answer the question that actually
 * gets asked halfway through an afternoon: *what have I done, and can I get back to how it looked
 * before I started fiddling with the ground?* Answering that with Ctrl+Z means pressing it blind and
 * watching the site jump, counting steps you cannot see. A list turns it into reading and one click.
 *
 * So this is Paint.NET's history window, and follows it closely because the conventions are worth
 * more than novelty here: oldest at the top, newest at the bottom, the current state highlighted,
 * everything undone still listed and greyed rather than deleted, and clicking any row travels to it.
 * A user who has used an image editor knows how to work this panel before they open it.
 *
 * ## Non-modal, on purpose
 *
 * It floats over the palette with the scene live behind it, and it never takes the canvas — see the
 * `VIEW_AID_ACTIONS` list in `Hud.ts`. Travelling the history while a tool is in hand is the normal
 * case, not an edge one: you paint, you dislike it, you step back three, you carry on painting. A
 * panel that put the brush down to be read would be a panel you close before using the brush again.
 *
 * ## Session-ephemeral
 *
 * Nothing here is saved. The history describes a path, a project file describes a garden, and the
 * two have different lifetimes — see the note on {@link CommandHistory}.
 */

export interface HistoryPanelDeps {
  /** The one timeline the whole editor shares — planting, ground, structures, tags, the wand. */
  history: CommandHistory;
  /** Where the floating card mounts. The HUD root, which is already stretched over the canvas. */
  host: HTMLElement;
  /**
   * Travel to a point in the session.
   *
   * Routed through the editor rather than calling `history.goto` here, because arriving somewhere
   * else in the session is not only a model change: the selection may now name plants that do not
   * exist, and the highlight renderer has to be told. The editor owns both of those.
   */
  goto: (index: number) => void;
  /** Told when the panel opens or closes, so the toolbar button can show itself pressed. */
  onVisibilityChange?: () => void;
}

/** How often the "8 min ago" lines are re-read while the panel is open. */
const CLOCK_INTERVAL_MS = 30_000;

type HistoryButtonName = 'rewind' | 'undo' | 'redo' | 'forward' | 'clear';

export class HistoryPanel {
  readonly root: HTMLElement;

  private readonly summary: HTMLElement;
  private readonly list: HTMLElement;
  private readonly note: HTMLElement;
  private readonly buttons = new Map<HistoryButtonName, HTMLButtonElement>();
  private readonly unsubscribe: (() => void)[] = [];

  private frameHandle: number | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Escape closes the panel, claimed on `window` in the capture phase for the same two reasons the
   * manifest claims it there: a rebuild replaces the element that had focus, so a listener on the
   * card would stop hearing the key; and capture puts it ahead of the editor's own handler, so the
   * press closes the panel rather than clearing the selection behind it.
   */
  private readonly onWindowKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    event.preventDefault();
    this.close();
  };

  constructor(private readonly deps: HistoryPanelDeps) {
    this.summary = el('span', { class: 'lg-history__summary' });
    this.list = el('div', {
      class: 'lg-history__list',
      attrs: { role: 'listbox', 'aria-label': 'Operations, oldest first' },
      on: { keydown: (event) => this.handleListKey(event) },
    });
    this.note = el('span', { class: 'lg-history__note' });

    this.root = el(
      'section',
      {
        class: 'lg-history',
        attrs: { 'aria-label': 'Operations history', hidden: true },
      },
      el(
        'header',
        { class: 'lg-history__head' },
        el('span', { class: 'lg-history__headicon', html: icon('history', 16) }),
        el(
          'div',
          { class: 'lg-history__heading' },
          el('h2', { class: 'lg-history__title', text: 'History' }),
          this.summary,
        ),
        el('button', {
          class: 'lg-history__close',
          html: icon('close', 14),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the history' },
          on: { click: () => this.close() },
        }),
      ),
      this.list,
      this.buildFooter(),
    );

    deps.host.appendChild(this.root);

    this.unsubscribe.push(deps.history.onChange(() => this.requestRender()));
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onWindowKeyDown, true);
      this.unsubscribe.push(() => window.removeEventListener('keydown', this.onWindowKeyDown, true));
    }
  }

  get isOpen(): boolean {
    return this.root.hidden === false;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    if (this.isOpen) return;
    this.root.hidden = false;
    this.render();
    // Only while open: a relative-time line that is not on screen is not worth a timer.
    this.clockTimer = setInterval(() => this.render(), CLOCK_INTERVAL_MS);
    this.deps.onVisibilityChange?.();
  }

  close(): void {
    if (!this.isOpen) return;
    this.root.hidden = true;
    if (this.clockTimer !== null) clearInterval(this.clockTimer);
    this.clockTimer = null;
    this.deps.onVisibilityChange?.();
  }

  /* ---------------------------------- Construction ---------------------------------- */

  private buildFooter(): HTMLElement {
    const transport = el('div', { class: 'lg-history__transport' });
    for (const [name, iconName, label, title, run] of [
      ['rewind', 'rewind', 'Rewind', 'Back to the start of the session', () => this.deps.goto(0)],
      ['undo', 'undo', 'Undo', 'Undo one step  ·  Ctrl+Z', () => this.step(-1)],
      ['redo', 'redo', 'Redo', 'Redo one step  ·  Ctrl+Shift+Z', () => this.step(1)],
      [
        'forward',
        'forward',
        'Fast forward',
        'Forward past everything undone',
        () => this.deps.goto(this.deps.history.count),
      ],
    ] as const) {
      const button = el('button', {
        class: 'lg-history__button',
        html: icon(iconName, 15),
        title,
        attrs: { type: 'button', 'aria-label': label },
        on: { click: run },
      });
      this.buttons.set(name, button);
      transport.appendChild(button);
    }

    const clearButton = el('button', {
      class: 'lg-history__clear',
      text: 'Clear',
      title: 'Forget these steps. The site is left exactly as it is — only the record goes.',
      attrs: { type: 'button' },
      on: { click: () => this.clearHistory() },
    });
    this.buttons.set('clear', clearButton);

    return el(
      'footer',
      { class: 'lg-history__foot' },
      transport,
      this.note,
      el('div', { class: 'lg-history__spacer' }),
      clearButton,
    );
  }

  /* ------------------------------------ Rendering ------------------------------------ */

  private requestRender(): void {
    if (!this.isOpen || this.frameHandle !== null) return;
    const schedule =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (callback: FrameRequestCallback): number =>
            setTimeout(() => callback(0), 16) as unknown as number;
    this.frameHandle = schedule(() => {
      this.frameHandle = null;
      this.render();
    });
  }

  private render(): void {
    const { history } = this.deps;
    const state = history.state();
    const rows = historyRows(history.entries(), state.cursor, { truncated: state.truncated });

    this.summary.textContent = historySummary(state);

    // Which row had the keyboard before the rebuild, so a repaint under the user's hands does not
    // dump focus back to the top of the list.
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const hadFocus = active instanceof HTMLElement && this.list.contains(active);
    const focusIndex = hadFocus ? Number(active.dataset.index ?? '0') : null;

    clear(this.list);
    let current: HTMLElement | null = null;
    for (const row of rows) {
      const element = this.rowElement(row);
      if (row.state === 'current') current = element;
      this.list.appendChild(element);
    }

    if (focusIndex !== null) {
      this.list.querySelector<HTMLElement>(`[data-index="${focusIndex}"]`)?.focus();
    }

    // Keep the place you are actually at on screen. `nearest` rather than `center` so a click two
    // rows up does not throw the whole list about under the pointer.
    current?.scrollIntoView({ block: 'nearest' });

    this.setEnabled('rewind', state.cursor > 0);
    this.setEnabled('undo', state.canUndo);
    this.setEnabled('redo', state.canRedo);
    this.setEnabled('forward', state.cursor < state.count);
    this.setEnabled('clear', state.count > 0);

    this.note.textContent =
      state.count === 0
        ? 'Every edit lands here'
        : state.truncated
          ? 'Older steps have been dropped'
          : '';
  }

  private setEnabled(name: HistoryButtonName, enabled: boolean): void {
    const button = this.buttons.get(name);
    if (button) button.disabled = !enabled;
  }

  private rowElement(row: HistoryRow): HTMLElement {
    const element = el(
      'button',
      {
        class: `lg-history__row is-${row.state}${row.isBaseline ? ' is-baseline' : ''}`,
        title: historyRowTitle(row),
        attrs: {
          type: 'button',
          role: 'option',
          'aria-selected': row.state === 'current',
          'data-index': row.index,
          // Only the current row is in the tab order; the arrow keys walk the rest. A hundred stops
          // between the list and the buttons under it would make Tab useless.
          tabindex: row.state === 'current' ? 0 : -1,
        },
        on: { click: () => this.deps.goto(row.index) },
      },
      el('span', {
        class: 'lg-history__glyph',
        html: icon(row.isBaseline ? 'open' : historyIcon(row.kind), 14),
      }),
      el(
        'span',
        { class: 'lg-history__text' },
        el('span', { class: 'lg-history__label', text: row.label }),
        el('span', { class: 'lg-history__detail', text: row.detail }),
      ),
    );
    return element;
  }

  /* ---------------------------------- Interaction ---------------------------------- */

  /** Move one step from wherever the cursor is, without going through the keyboard shortcut. */
  private step(direction: -1 | 1): void {
    this.deps.goto(this.deps.history.cursor + direction);
  }

  /**
   * Arrow keys walk the list; Enter and Space travel to the focused row.
   *
   * Walking is deliberately separate from travelling. Arrowing through a hundred rows and having
   * the site rebuild at each one would be unusable on a large site, and would fire a hundred
   * pointless journeys to reach the one you wanted.
   */
  private handleListKey(event: KeyboardEvent): void {
    const rows = [...this.list.querySelectorAll<HTMLElement>('.lg-history__row')];
    if (rows.length === 0) return;
    const active = document.activeElement;
    const from = active instanceof HTMLElement ? rows.indexOf(active) : -1;

    let next: number | null = null;
    switch (event.key) {
      case 'ArrowDown':
        next = Math.min(rows.length - 1, from + 1);
        break;
      case 'ArrowUp':
        next = from <= 0 ? 0 : from - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = rows.length - 1;
        break;
      case 'Enter':
      case ' ': {
        if (from < 0) return;
        event.preventDefault();
        this.deps.goto(Number(rows[from]?.dataset.index ?? '0'));
        return;
      }
      default:
        return;
    }

    event.preventDefault();
    const target = rows[next];
    if (!target) return;
    for (const row of rows) row.tabIndex = row === target ? 0 : -1;
    target.focus();
  }

  /**
   * Drop the record without touching the document.
   *
   * Confirmed first, because it is the one control here that cannot itself be undone — every other
   * button in this panel is reversible by definition.
   */
  private clearHistory(): void {
    if (this.deps.history.count === 0) return;
    const ok =
      typeof window === 'undefined' ||
      typeof window.confirm !== 'function' ||
      window.confirm(
        'Forget the operations history?\n\n' +
          'Your site is left exactly as it is — only the record of how it got here goes, ' +
          'and undo will start again from this point.',
      );
    if (!ok) return;
    this.deps.history.clear();
    this.render();
  }

  dispose(): void {
    if (this.frameHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.frameHandle);
    }
    this.frameHandle = null;
    if (this.clockTimer !== null) clearInterval(this.clockTimer);
    this.clockTimer = null;
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.buttons.clear();
    this.root.remove();
  }
}
